import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { FormInputProps } from "../../models/form.model";
import { difference } from "lodash";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  style: {
    zIndex: 1302,
  },
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface SelectionOption {
  label: string | React.ReactNode;
  image?: string;
  value: string | number | boolean;
  disabled?: boolean;
  noCheckbox?: boolean;
  groupPrefix?: string;
}

export default function FormSelection(
  props: FormInputProps & {
    options: SelectionOption[];
    noEmpty?: boolean;
    emptyText?: string;
    multiple?: boolean;
    disabled?: boolean;
    dataTestId?: string;
    onValueParse?: (value: string) => string | number | boolean;
    size?: "small" | "medium";
  }
) {
  const {
    name,
    control,
    emptyText,
    noEmpty,
    label,
    helperText,
    rules,
    required,
    multiple,
    options,
    setError,
    disabled,
    dataTestId,
    onValueParse,
    defaultValue,
    onChange: onChangeProp,
    sx,
    size,
  } = props;
  const setErrorMsg = (message: string) => {
    setError(name, { type: "manual", message });
  };
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field: { onChange, value: values = multiple ? [] : "" }, fieldState: { error }, formState }) => {
        if (multiple) {
          values = Array.isArray(values) ? values : [];
        } else {
          values = Array.isArray(values) ? "" : values;
        }
        return (
          <FormControl error={!!error} required={required} className="width-100p" sx={sx}>
            <InputLabel>{label}</InputLabel>
            <Select
              multiple={multiple}
              autoComplete="off"
              value={values}
              disabled={disabled}
              size={size}
              sx={{
                ".MuiSelect-multiple.MuiInputBase-input": {
                  whiteSpace: "normal",
                },
              }}
              onChange={(e) => {
                const {
                  target: { value },
                } = e;
                let newValues = multiple && typeof value === "string" ? value.split(",") : value;
                const oldValues = values;
                const addedValue = difference(newValues, oldValues)[0];
                const option = options.find((op) => op.value.toString() === addedValue);
                if (multiple && option?.groupPrefix && addedValue) {
                  const groupPrefix = option.groupPrefix;
                  const optionValue = option.value;
                  newValues = newValues.filter((v: string) => v === optionValue || !v.startsWith(groupPrefix));
                }
                const parsedValue = onValueParse ? onValueParse(newValues) : newValues
                onChange(parsedValue);
                onChangeProp?.(parsedValue);
              }}
              input={<OutlinedInput label={label} />}
              renderValue={(selected) => {
                return options
                  .filter((a) => (multiple ? ~selected.indexOf(`${a.value}`) : selected == a.value))
                  .map((v) => v.label)
                  .join(", ");
              }}
              MenuProps={MenuProps}
              data-testid={dataTestId || name}
            >
              {!noEmpty && (
                <MenuItem value="">
                  <em>{emptyText || "None"}</em>
                </MenuItem>
              )}

              {options.map((op, i) => (
                <MenuItem
                  disabled={op.disabled}
                  key={op.value + "_" + i}
                  value={op.value.toString()}
                  style={op.noCheckbox ? { pointerEvents: "none" } : {}}
                  data-testid={`${dataTestId || name}-option${i}`}
                >
                  {multiple && !op.noCheckbox && (
                    <Checkbox checked={values && values.indexOf(op.value.toString()) > -1} />
                  )}
                  <ListItemText primary={op.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{error ? error.message : helperText || null}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}
